import React, { FC, useEffect, useRef, useState } from "react";
import './Clause.scss';
import { IClause } from "../../Interfaces";
import { ReactSVG } from "react-svg";
import redraftIcon from "../../../../Assets/icons/redraft.svg";
import deleteIcon from "../../../../Assets/icons/delete.svg";
import listIcon from "../../../../Assets/icons/list-bullets.svg";
import { useDispatch, useSelector } from "react-redux";
import {draftingSlice, selectRedraftingClause } from "../../../../Store/draftingSlice";
import { AppDispatch } from "../../../../Store/store";
import { useTranslation } from "react-i18next";
import TextEditor from "../../../../Components/TextEditor/TextEditor";
import RestrictReadOnly from "../../../../Helpers/RestrictReadOnly";
import Modal from "../../../../Components/Modal/Modal";
import Button, { EButtonType } from "../../../../Components/Buttons/Button";
import { useEventSubscriber } from "../../../../Api/ApiEvents/ApiEventSubscriber";
import { set } from "date-fns";

interface IClauseProps {
    clause: IClause;
    isDrafting?: boolean;
    instructionsRequired?: boolean;
    updateClauseBySequence: (sequence: number, clause: IClause) => void;
    deleteClauseById: (id: number) => void;
}

const Clause: FC<IClauseProps> = ({
    clause,
    isDrafting,
    instructionsRequired,
    updateClauseBySequence,
    deleteClauseById,
}) => {
    const clauseRef = useRef<HTMLDivElement>();

    useEffect(() => {
        if(clauseRef.current)
            clauseRef.current.innerHTML = clause.content;
    }, [clause, clauseRef, instructionsRequired]);

    const { t } = useTranslation();

    const redraftingClause = useSelector(selectRedraftingClause);

    const dispatch = useDispatch<AppDispatch>();

    const [isSelected, setIsSelected] = useState(redraftingClause?.id === clause?.id);

    useEffect(() => {
        setIsSelected(redraftingClause?.id === clause?.id);
    }, [redraftingClause, clause]);

    const onClauseBlur = (event?: React.FocusEvent<HTMLDivElement>) => {

        if (event && clauseRef.current && clauseRef.current.contains(event.relatedTarget as Node)) {
            return;
        }

        if (event && event.target && (event.target as HTMLElement).closest('.start-instructions')) {
            return;
        }
        if(clauseRef.current) {
            let htmlContent = clauseRef.current.innerHTML;

            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = htmlContent;

            if (clause.content !== tempDiv.innerHTML) {
                updateClauseBySequence(clause.sequence, { ...clause, content: tempDiv.innerHTML });
            }
        }

        if(redraftingClause?.id !== clause?.id) {
            setIsSelected(false);
        }
    };

    const wrapperRef = useRef<HTMLDivElement>(null);

    const [ showInstructions, setShowInstructions ] = useState(false);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {

            if (
                isSelected && 
                wrapperRef.current && 
                !wrapperRef.current.contains(event.target as Node) &&
                !showInstructions

            ) {
                onClauseBlur(event as unknown as React.FocusEvent<HTMLDivElement>);
            }
        };
      
        document.addEventListener("mousedown", handleClickOutside);
      
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isSelected, redraftingClause, clause, showInstructions]);

    useEventSubscriber("clause", clause.id, (data: any) => {
        updateClauseBySequence(clause.sequence, { ...clause, content: data.content, instructions_complete: data.instructions_complete });
        dispatch(draftingSlice.actions.setRedraftingClause(null));
    });

    const Instructions = (props) => {

        const { list } = props;

        const [ instructions, setInstructions ] = useState(list);

        const saveInstructions = () => {
            updateClauseBySequence(clause.sequence, { ...clause, instructions });
        };

        const MAX_LINES = 100;
        const [ lineLimitReached, setLineLimitReached ] = useState(false);

        const textAreaRef = useRef(null);
        const lineNumbersRef = useRef(null);    
        const [text, setText] = useState(instructions.join('\n'));
        const [lineHeights, setLineHeights] = useState([0]);

        const handleChange = (event) => {
            const newText = event.target.value;
            const newLines = newText.split('\n');

            if (newLines.length <= MAX_LINES) {
                setLineLimitReached(false);
                setText(newText);
            } else {
                setLineLimitReached(true);
                setText(newText.split('\n').splice(0, MAX_LINES).join('\n'));
            }
        };

        useEffect(() => {

            if(text !== "") return;

            if(!instructions) {
                setText(" ");
                return;
            }
            setText(instructions.slice(0, MAX_LINES).join('\n'));
        }, [instructions]);

        useEffect(() => {

            const newLines = text.split('\n').slice(0, MAX_LINES);

            setInstructions(newLines);

            if (textAreaRef.current && lineNumbersRef.current) {

                const mirrorDiv = document.createElement('div');
                mirrorDiv.style.position = 'absolute';
                mirrorDiv.style.visibility = 'hidden';
                mirrorDiv.style.whiteSpace = 'pre-wrap';
                mirrorDiv.style.wordWrap = 'break-word';
                mirrorDiv.style.width = `${textAreaRef?.current?.clientWidth}px`;
                mirrorDiv.style.fontFamily = window.getComputedStyle(textAreaRef.current).fontFamily;
                mirrorDiv.style.fontSize = window.getComputedStyle(textAreaRef.current).fontSize;
                mirrorDiv.style.lineHeight = window.getComputedStyle(textAreaRef.current).lineHeight;
                mirrorDiv.style.padding = window.getComputedStyle(textAreaRef.current).padding;
                mirrorDiv.style.boxSizing = 'border-box';
                document.body.appendChild(mirrorDiv);
            
                const heights = newLines.map((line) => {
                mirrorDiv.innerText = line || ' ';
                return mirrorDiv.scrollHeight - 4;
                });
            
                setLineHeights(heights);
                document.body.removeChild(mirrorDiv);

                textAreaRef.current.style.height = 'auto';
                textAreaRef.current.style.height = `${textAreaRef.current.scrollHeight + 2}px`;
                lineNumbersRef.current.style.height = `${textAreaRef.current.scrollHeight + 1}px`;
            }

        }, [text, textAreaRef.current, lineNumbersRef.current]);

        return (
            <div className="instruction-modal">
                <div className="line-management">
                    <div className="line-numbers" ref={lineNumbersRef}>
                        {lineHeights.map((height, index) => (
                            <div key={index} className="line-number" style={{ height: `${height}px` }}>{index + 1}</div>
                        ))}
                    </div>
                    <textarea
                        ref={textAreaRef}
                        className="textarea"
                        value={text}
                        onChange={handleChange}
                    />
                </div>
                <Button
                    className="button"
                    text="Save"
                    buttonType={EButtonType.PRIMARY}
                    onClick={() => {
                        saveInstructions();
                        setShowInstructions(false);
                    }}
                />
            </div>
        );
    }

    useEffect(() => {
        if(isSelected)
            setTimeout(() => {
                wrapperRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                if(!instructionsRequired) {
                    setTimeout(() => {
                        dispatch(draftingSlice.actions.setRedraftingClause(null));
                    }, 2000);
                }
            }, 100);
        }
    , [isSelected, wrapperRef]);

    return (
        <div
            className={`editable-div-wrapper ${isSelected ? 'selected' : ''}`}
            ref={wrapperRef}
        >
            <Modal
                showModal={showInstructions}
                setShowModal={setShowInstructions}
                title={"Instructions"}
                content={<Instructions list={clause?.instructions || []} />}
            />
            {(!isDrafting || !instructionsRequired) ?
                <div
                    id={`clause-${clause.id}-content`}
                    className={`editable-div ${isSelected ? "selected" : "not-selected"}`}
                    contentEditable="true"
                    onMouseDown={(event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
                        setIsSelected(true);
                    }}
                    ref={clauseRef}
                />
                : ( isSelected &&
                    <div className="instructions-not-complete">
                        {t('drafting.doingInstructions')}
                    </div>
                )
            }
            {isSelected && (!isDrafting || !clause?.instructions || (clause?.instructions && clause?.instructions_complete)) &&<div className="clause-action-buttons-container">
                <div className="clause-action-buttons-wrapper">
                    <div className="clause-action-buttons">
                        <TextEditor />
                        {!isDrafting && <div className={`delete`} onMouseDown={() => setShowInstructions(true)}><ReactSVG src={listIcon} />
                            Instructions
                        </div>}
                    </div>
                </div>
            </div>}
        </div>
    );
};

export default Clause;